// @ts-check
/** What "Cancel Order" should actually do, given the order's current state. Cancelling always
 * restores stock (handled server-side by PATCH .../status). Whether it should ALSO refund
 * depends on how the order was paid, whether it's only partially paid, and whether any piece
 * on it was customized:
 *  - a customized piece (measurements or a fit note on any item) is never refunded, however it
 *    was paid - it can't be resold as regular stock once made to the customer's own measurements.
 *  - never paid: nothing to refund.
 *  - partially paid: always manual - a partial payment only ever uses cash/UPI/bank transfer
 *    (never a pending gateway link), so there's nothing to trigger via API; refund what was
 *    actually collected (amount_paid), not the full order total.
 *  - paid via a real payment gateway (razorpay_payment_id set): the same PATCH
 *    call can also set payment_status: 'refunded', which triggers a real gateway refund - bundle
 *    it into one action instead of two separate admin steps.
 *  - paid by cash/UPI (admin-marked, no gateway transaction): there's nothing to trigger via
 *    API - the admin has to refund the customer themselves, so say so plainly rather than
 *    silently doing nothing.
 */

/**
 * @param {{order_status?: string, payment_status?: string, total?: number, amount_paid?: number, razorpay_payment_id?: string | null, items?: Array<{custom_measurements?: any, fit_notes?: string | null}>}} order
 */
export function cancelOrderPlan(order) {
  const inr = (amount) => `₹${Number(amount).toLocaleString('en-IN')}`;
  const alreadyCancelled = order.order_status === 'cancelled';
  if (alreadyCancelled) {
    return { alreadyCancelled: true, payload: null, confirmMessage: 'This order is already cancelled.' };
  }

  const wasPaid = order.payment_status === 'paid';
  const wasPartiallyPaid = order.payment_status === 'partially_paid';
  const hasGatewayPayment = !!order.razorpay_payment_id;
  const isCustomized = (order.items || []).some((item) => item.custom_measurements || item.fit_notes);
  const autoRefundable = wasPaid && hasGatewayPayment && !isCustomized;

  const payload = { order_status: 'cancelled', ...(autoRefundable ? { payment_status: 'refunded' } : {}) };

  let confirmMessage;
  if (!wasPaid && !wasPartiallyPaid) {
    confirmMessage = 'Cancel this order? Stock will be restored.';
  } else if (isCustomized) {
    const amount = wasPartiallyPaid ? order.amount_paid : order.total;
    confirmMessage =
      `Cancel this order? It has a custom/made-to-order piece, so ${inr(amount)} already paid is not ` +
      'refunded. Stock will be restored.';
  } else if (autoRefundable) {
    confirmMessage = `Cancel this order and refund ${inr(order.amount_paid ?? order.total)} via the payment gateway? Stock will be restored.`;
  } else if (wasPartiallyPaid) {
    confirmMessage =
      `Cancel this order? Stock will be restored. ${inr(order.amount_paid)} has been collected so far - ` +
      "you'll need to refund this manually; there's no automatic way to do this.";
  } else {
    confirmMessage =
      "Cancel this order? Stock will be restored, but this was paid by cash/UPI — you'll need to refund the " +
      "customer manually; there's no automatic way to do this.";
  }

  return { alreadyCancelled: false, payload, confirmMessage };
}
